'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Navigation } from '@/components/Navigation'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { ArrowRight, ArrowUpRight, Atom, Boxes, BrainCircuit, Calculator, Cpu, Database, Fingerprint, MoveUpRight, Network, Radio, Sparkles, TestTubes, Zap } from 'lucide-react'

type Module = {
  code: string
  name: string
  blurb: string
  href: string
  icon: typeof BrainCircuit
}

const MODULES: Module[] = [
  {
    code: 'AGT-01',
    name: 'OligoLLM',
    blurb:
      'Ask anything about polymers — get a grounded answer with checkable references. No invented citations.',
    href: '/tools/oligollm',
    icon: BrainCircuit,
  },
  {
    code: 'FRM-02',
    name: 'OligoFormulizer',
    blurb: 'Predict, then optimise — find a recipe that hits every target.',
    href: '/tools/oligoformulizer',
    icon: Atom,
  },
  {
    code: 'DOE-03',
    name: 'OligoDOE',
    blurb: 'Plan smarter — extract maximum insight from the fewest runs.',
    href: '/tools/oligodoe',
    icon: Network,
  },
  {
    code: 'MTC-04',
    name: 'OligoMatch',
    blurb: 'Identify and compare materials in seconds — across performance and cost.',
    href: '/tools/oligomatch',
    icon: Fingerprint,
  },
  {
    code: 'PRM-05',
    name: 'OligoParaspace',
    blurb: 'Sweep every parameter combination — find sweet spots fast.',
    href: '/tools/oligoparaspace',
    icon: Boxes,
  },
  {
    code: 'ECO-06',
    name: 'OligoNomics',
    blurb: 'Cost, margin and break-even — economics against a live pricing catalog.',
    href: '/tools/oligonomics',
    icon: Calculator,
  },
  {
    code: 'LAB-08',
    name: 'β-OligoLLM',
    blurb: 'The assistant with a proposed search change, and the bench that judges it.',
    href: '/tools/betaoligollm',
    icon: TestTubes,
  },
  {
    code: 'SCL-07',
    name: 'OligoScaleUp',
    blurb: 'Move a recipe between mixers — settings, with the reasoning shown.',
    href: '/tools/oligoscaleup',
    icon: MoveUpRight,
  },
]

export default function Home() {
  const { user } = useAuth()
  const [now, setNow] = useState<Date | null>(null)

  useEffect(() => {
    setNow(new Date())
    const id = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(id)
  }, [])

  const firstName =
    user?.full_name?.trim().split(/\s+/)[0] ||
    user?.email?.split('@')[0] ||
    'Researcher'

  const sid =
    user?.id != null
      ? user.id.toString(16).toUpperCase().padStart(4, '0')
      : '0001'

  const stamp = now
    ? `${now.toISOString().slice(0, 10)} · ${now
        .toISOString()
        .slice(11, 16)} UTC`
    : '— · —'

  return (
    <ProtectedRoute>
      <div className="relative min-h-screen overflow-hidden bg-slate-25 text-slate-700">
        {/* atmosphere */}
        <div className="pointer-events-none fixed inset-0 blueprint opacity-60" />
        <div className="pointer-events-none fixed inset-x-0 top-0 h-[80vh] glow-teal" />

        <div className="relative">
          <Navigation />

          {/* ═══════════ STATUS RIBBON ═══════════ */}
          <div className="border-b border-slate-200/70 bg-slate-25/60 backdrop-blur-sm">
            <div className="mx-auto flex h-9 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                <span className="flex items-center gap-2">
                  <span className="relative inline-flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                  workspace online
                </span>
                <span className="hidden sm:inline">node · oligomaster-app</span>
                <span className="hidden lg:inline">{stamp}</span>
              </div>
              <div className="hidden font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400 md:flex md:items-center md:gap-5">
                <span>session · 0x{sid}</span>
                <span>06/06 modules</span>
                <span>offline · 100%</span>
              </div>
            </div>
          </div>

          {/* ═══════════ HERO CONSOLE ═══════════ */}
          <section className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 pt-16 pb-20 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pt-20 lg:pb-24">
            {/* LEFT — copy stack inside a register-marked panel */}
            <div className="relative lg:col-span-7">
              <RegisterMarks />

              <div
                className="data-tag mb-6 inline-flex animate-fade-in items-center gap-3 rounded-full border border-slate-200 bg-slate-100/60 px-3 py-1"
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span>WORKSPACE</span>
                <span className="inline-block h-3 w-px bg-slate-300" />
                <span>SESSION 0X{sid}</span>
              </div>

              <h1
                className="font-display text-5xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl animate-fade-in"
                style={{ animationDelay: '60ms' }}
              >
                Welcome back,
                <br />
                <span className="relative inline-block bg-gradient-to-br from-slate-900 via-primary to-accent bg-clip-text pr-2 text-transparent">
                  {firstName}
                  <span className="text-secondary">.</span>
                </span>
              </h1>

              <p
                className="mt-7 max-w-xl text-lg leading-relaxed text-slate-600 animate-fade-in"
                style={{ animationDelay: '140ms' }}
              >
                Your private R&amp;D bench is ready. Resume an open
                experiment, ask the cited research agent, or sweep a new
                design space — everything runs on this machine.
              </p>

              <div
                className="mt-9 flex flex-wrap items-center gap-3 animate-fade-in"
                style={{ animationDelay: '220ms' }}
              >
                <Link href="/dashboard">
                  <Button size="lg" className="gap-2 shadow-glow">
                    Resume work <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link href="/tools/oligollm">
                  <Button size="lg" variant="outline" className="gap-2">
                    <BrainCircuit className="h-4 w-4" /> Ask OligoLLM
                  </Button>
                </Link>
                <Link href="/tools">
                  <Button
                    size="lg"
                    variant="ghost"
                    className="gap-2 text-slate-600"
                  >
                    All modules
                  </Button>
                </Link>
              </div>

              <div
                className="mt-12 grid max-w-xl grid-cols-2 gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200 sm:grid-cols-4 animate-fade-in"
                style={{ animationDelay: '300ms' }}
              >
                {[
                  { k: 'modules', v: '06' },
                  { k: 'agents', v: '01' },
                  { k: 'ai answers', v: 'cited' },
                  { k: 'runs', v: 'offline' },
                ].map((s) => (
                  <div key={s.k} className="bg-slate-50 px-4 py-3">
                    <div className="font-display text-xl font-bold tabular-nums text-slate-900">
                      {s.v}
                    </div>
                    <div className="data-tag mt-0.5 text-slate-500">{s.k}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — orbital signature */}
            <div className="relative flex items-center justify-center lg:col-span-5">
              <OrbitalSignature />
            </div>
          </section>

          {/* ═══════════ INSTRUMENT RACK ═══════════ */}
          <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
            <div className="mb-8 flex items-end justify-between border-b border-slate-200 pb-4">
              <div>
                <span className="data-tag text-slate-500">
                  / MODULE&nbsp;RACK
                </span>
                <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900">
                  Pick a module
                </h2>
              </div>
              <Link
                href="/tools"
                className="hidden items-center gap-1.5 text-sm text-slate-600 transition-colors hover:text-secondary sm:inline-flex"
              >
                Open the module rack <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200 md:grid-cols-3 md:auto-rows-[minmax(0,1fr)] md:[grid-auto-flow:dense]">
              {/* Featured: OligoLLM */}
              <Link
                href={MODULES[0].href}
                className="group relative col-span-1 row-span-1 flex flex-col overflow-hidden bg-slate-50 p-8 transition-colors hover:bg-slate-100 animate-fade-in md:col-span-2 md:row-span-2"
                style={{ animationDelay: '60ms' }}
              >
                <div className="ds-scanline" aria-hidden />
                <div className="relative mb-6 flex items-center justify-between">
                  <span className="data-tag text-secondary">
                    FEATURED · {MODULES[0].code}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400">
                    cited literature
                  </span>
                </div>
                <div className="relative flex h-16 w-16 items-center justify-center rounded-md border border-secondary/40 bg-slate-100 text-secondary shadow-glow">
                  <BrainCircuit className="h-7 w-7" />
                </div>
                <h3 className="relative mt-6 font-display text-4xl font-bold tracking-tight text-slate-900 lg:text-5xl">
                  OligoLLM
                </h3>
                <p className="relative mt-3 max-w-md text-base leading-relaxed text-slate-600">
                  Ask anything about polymers and materials — get a clear,
                  grounded answer backed by real, checkable references. No
                  invented citations, no fishy summaries.
                </p>
                <div className="relative mt-auto flex items-center justify-between pt-6">
                  <div className="flex flex-wrap gap-1.5">
                    {['cited answers', 'literature', 'your documents'].map(
                      (t) => (
                        <span
                          key={t}
                          className="rounded border border-slate-200 bg-slate-100 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-slate-500"
                        >
                          {t}
                        </span>
                      ),
                    )}
                  </div>
                  <ArrowRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-secondary" />
                </div>
              </Link>

              {/* Secondary cells */}
              {MODULES.slice(1).map(({ name, code, blurb, href, icon: Icon }, i) => (
                <Link
                  key={name}
                  href={href}
                  className="group relative flex flex-col bg-slate-50 p-6 transition-colors hover:bg-slate-100 animate-fade-in"
                  style={{ animationDelay: `${140 + i * 60}ms` }}
                >
                  <div className="mb-4 flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md border border-secondary/40 bg-slate-100 text-secondary transition-shadow group-hover:shadow-glow">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="data-tag text-slate-500">{code}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold tracking-tight text-slate-900">
                    {name}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    {blurb}
                  </p>
                  <ArrowRight className="mt-auto self-end h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-secondary" />
                </Link>
              ))}
            </div>
          </section>

          {/* ═══════════ TELEMETRY STRIP ═══════════ */}
          <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200 lg:grid-cols-4">
              {[
                { icon: Radio, label: 'modules online', value: '06 / 06', sub: 'all green' },
                { icon: Cpu, label: 'compute', value: 'local', sub: 'no external API' },
                { icon: Database, label: 'data residency', value: 'on-prem', sub: 'never leaves the host' },
                { icon: Zap, label: 'session', value: 'active', sub: stamp },
              ].map(({ icon: Icon, label, value, sub }) => (
                <div key={label} className="bg-slate-50 px-5 py-4">
                  <div className="flex items-center gap-2 text-secondary">
                    <Icon className="h-4 w-4" />
                    <span className="data-tag text-slate-500">{label}</span>
                  </div>
                  <div className="mt-2 font-display text-2xl font-bold tabular-nums text-slate-900">
                    {value}
                  </div>
                  <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                    {sub}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ═══════════ SIGNAL TRACE ═══════════ */}
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SignalTrace />
          </div>

          {/* ═══════════ FOOTER ═══════════ */}
          <footer className="mt-2 border-t border-slate-200 bg-slate-50">
            <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500 sm:flex-row sm:px-6 lg:px-8">
              <span>Oligomaster Inc. · Designer Space™</span>
              <span className="text-slate-400">private · offline · v1.1.0</span>
            </div>
          </footer>
        </div>
      </div>
    </ProtectedRoute>
  )
}

/* ────────────────── subcomponents ────────────────── */

function RegisterMarks() {
  return (
    <>
      <span className="pointer-events-none absolute -left-3 -top-3 h-3 w-3 border-l border-t border-slate-300" />
      <span className="pointer-events-none absolute -right-3 -top-3 h-3 w-3 border-r border-t border-slate-300" />
      <span className="pointer-events-none absolute -left-3 -bottom-3 h-3 w-3 border-l border-b border-slate-300" />
      <span className="pointer-events-none absolute -right-3 -bottom-3 h-3 w-3 border-r border-b border-slate-300" />
    </>
  )
}

function OrbitalSignature() {
  // One steady orbit (no more two crossing rings, no codes). Each
  // module is a full-name pill at its own angle on the same
  // ring; pills counter-rotate at the parent's rate so labels stay
  // upright in screen-space. The middle is the colored Designer Space
  // emblem itself — same identity as the workspace wordmark.
  return (
    <div
      className="relative aspect-square w-full max-w-[520px] animate-fade-in"
      style={{ animationDelay: '180ms' }}
    >
      {/* backdrop : blueprint rings, soft core glow, mono labels */}
      <svg viewBox="0 0 520 520" className="absolute inset-0 h-full w-full">
        <defs>
          <radialGradient id="dsCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgb(53 152 219 / 0.32)" />
            <stop offset="100%" stopColor="rgb(53 152 219 / 0)" />
          </radialGradient>
          <radialGradient id="dsHalo" cx="50%" cy="50%" r="50%">
            <stop offset="60%" stopColor="rgb(36 61 152 / 0)" />
            <stop offset="100%" stopColor="rgb(36 61 152 / 0.08)" />
          </radialGradient>
        </defs>
        <circle cx="260" cy="260" r="240" fill="url(#dsHalo)" />
        <circle cx="260" cy="260" r="80" fill="url(#dsCore)" />
        {[100, 150, 196, 240].map((r) => (
          <circle
            key={r}
            cx="260"
            cy="260"
            r={r}
            fill="none"
            stroke="rgb(36 61 152 / 0.10)"
            strokeWidth="1"
          />
        ))}
        {/* the visible orbit path */}
        <circle
          cx="260"
          cy="260"
          r="196"
          fill="none"
          stroke="rgb(36 61 152 / 0.25)"
          strokeWidth="1"
          strokeDasharray="3 6"
        />
        {/* crosshair */}
        <g stroke="rgb(36 61 152 / 0.4)" strokeWidth="1">
          <line x1="260" y1="244" x2="260" y2="276" />
          <line x1="244" y1="260" x2="276" y2="260" />
        </g>
        {/* corner ticks at canvas extremes */}
        <g stroke="rgb(36 61 152 / 0.32)" strokeWidth="1">
          <path d="M14 14 H30 M14 14 V30" fill="none" />
          <path d="M506 14 H490 M506 14 V30" fill="none" />
          <path d="M14 506 H30 M14 506 V490" fill="none" />
          <path d="M506 506 H490 M506 506 V490" fill="none" />
        </g>
        {/* arc text — top + bottom — mono brand labels */}
        <text
          x="260"
          y="42"
          textAnchor="middle"
          fontFamily="JetBrains Mono, ui-monospace"
          fontSize="10"
          letterSpacing="4"
          fill="rgb(36 61 152 / 0.50)"
        >
          OLIGOMASTER · INC.
        </text>
        <text
          x="260"
          y="498"
          textAnchor="middle"
          fontFamily="JetBrains Mono, ui-monospace"
          fontSize="10"
          letterSpacing="4"
          fill="rgb(36 61 152 / 0.55)"
        >
          DESIGNER · SPACE™
        </text>
      </svg>

      {/* spinning ring — one full-name pill per module, evenly spaced */}
      <div className="ds-orbit-ring absolute inset-0">
        {MODULES.map((m, i) => (
          <OrbitNode key={m.name} angle={(i * 360) / MODULES.length - 90} module={m} />
        ))}
      </div>

      {/* central Designer Space emblem (the real one, not a generic icon) */}
      <div className="ds-center absolute left-1/2 top-1/2 flex h-32 w-32 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-secondary/40 bg-slate-50/95 shadow-glow backdrop-blur emblem-float">
        <Image
          src="/ds-emblem.png"
          alt="Designer Space"
          width={88}
          height={88}
          priority
          className="h-22 w-22 object-contain"
        />
      </div>
    </div>
  )
}

/**
 * A single named bead on the orbit ring.
 *
 * Positioning:
 *   - parent ring rotates around its own centre via .ds-orbit-ring
 *   - each <OrbitNode> is statically placed at its `angle` on radius RING_R
 *   - the pill inside counter-rotates at the same duration so text stays upright
 */
function OrbitNode({ angle, module: m }: { angle: number; module: Module }) {
  const Icon = m.icon
  const RING_R = 196 // viewBox 520px / 2 - margin
  return (
    <div
      className="absolute left-1/2 top-1/2"
      style={{
        transform: `translate(-50%, -50%) rotate(${angle}deg) translate(${RING_R}px)`,
      }}
    >
      <div className="ds-orbit-counter">
        <Link
          href={m.href}
          title={m.name}
          className="group flex items-center gap-2 rounded-full border border-secondary/40 bg-slate-50/95 px-3.5 py-1.5 shadow-sm backdrop-blur-sm transition hover:border-secondary hover:bg-white hover:shadow-glow"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary/10 text-secondary">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <span className="font-display text-xs font-semibold tracking-tight text-slate-800 transition-colors group-hover:text-secondary">
            {m.name}
          </span>
        </Link>
      </div>
    </div>
  )
}

function SignalTrace() {
  return (
    <svg viewBox="0 0 1200 60" className="ds-trace h-12 w-full opacity-60">
      <defs>
        <linearGradient id="dsSig" x1="0" x2="1">
          <stop offset="0%" stopColor="rgb(36 61 152 / 0)" />
          <stop offset="15%" stopColor="rgb(36 61 152 / 0.5)" />
          <stop offset="85%" stopColor="rgb(36 61 152 / 0.5)" />
          <stop offset="100%" stopColor="rgb(36 61 152 / 0)" />
        </linearGradient>
      </defs>
      <path
        d="M0,30 Q60,8 120,30 T240,30 T360,30 T480,30 T600,30 T720,30 T840,30 T960,30 T1080,30 T1200,30"
        fill="none"
        stroke="url(#dsSig)"
        strokeWidth="1.4"
        strokeDasharray="6 6"
      />
    </svg>
  )
}
